import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import arrowIcon from '@assets/img/slick-next.svg';
import './style.scss'

const NestedCheckboxList = ({
    field,
    data,
    uniquePrefix,
    selected,
    expanded,
    onChange
}) => {
    const [expandedParent, setExpandedParent] = useState({})
    const [expandedChild, setExpandedChild] = useState({})

    const selectedValues = selected
        ? selected.split(';')
        : [];

    // Automatycznie rozwijaj listy z wybranymi elementami
    useEffect(() => {
        if (!selected || selected === '') return;

        const values = selected.split(';');
        const newExpandedParent = {};
        const newExpandedChild = {};

        values.forEach(value => {
            const parts = value.split('.');
            
            // Jeśli jest wybrana wartość z 2 lub więcej poziomami, rozwiń odpowiednie listy
            if (parts.length >= 2) {
                // Rozwiń pierwszy poziom (kraj)
                const country = parts[0];
                newExpandedParent[country] = true;
                
                // Jeśli jest 3 poziomy, rozwiń też drugi poziom (region)
                if (parts.length >= 3) {
                    const region = `${parts[0]}.${parts[1]}`;
                    newExpandedChild[region] = true;
                }
            }
        });

        setExpandedParent(prev => ({ ...prev, ...newExpandedParent }));
        setExpandedChild(prev => ({ ...prev, ...newExpandedChild }));
    }, [selected]);

    // Automatycznie rozwijaj wszystkie listy gdy expanded={true} (tryb filtrowania)
    useEffect(() => {
        if (!expanded || !data) return;

        const newExpandedParent = {};
        const newExpandedChild = {};

        // Rozwiń wszystkie kraje (pierwszy poziom)
        if (typeof data === 'object' && !Array.isArray(data)) {
            Object.keys(data).forEach(country => {
                newExpandedParent[country] = true;
                
                // Rozwiń też wszystkie regiony (drugi poziom) jeśli są
                const regions = data[country];
                if (regions && typeof regions === 'object' && !Array.isArray(regions)) {
                    Object.keys(regions).forEach(region => {
                        const regionKey = `${country}.${region}`;
                        newExpandedChild[regionKey] = true;
                    });
                }
            });
        }

        setExpandedParent(prev => ({ ...prev, ...newExpandedParent }));
        setExpandedChild(prev => ({ ...prev, ...newExpandedChild }));
    }, [expanded, data]);

    const handleToggle = (value) => {
        if (typeof onChange === 'function') {
            onChange(field, value)
        }
    }

    const toggleExpand = (parent) => {
        setExpandedParent((prev) => ({
            ...prev,
            [parent]: !prev[parent]
        }))
    }

    const toggleExpandChild = (childKey) => {
        setExpandedChild((prev) => ({
            ...prev,
            [childKey]: !prev[childKey]
        }))
    }

    const isObject = (value) => {
        return value !== null && typeof value === 'object' && !Array.isArray(value)
    }

    const isArrayOfStrings = (value) => {
        return Array.isArray(value) && (value.length === 0 || typeof value[0] === 'string')
    }

    // Sprawdza czy children nie są puste (tablica lub obiekt)
    const hasNonEmptyChildren = (children) => {
        if (!children) return false
        if (Array.isArray(children)) {
            return children.length > 0
        }
        if (isObject(children)) {
            return Object.keys(children).length > 0
        }
        return false
    }

    const hasSelectedAncestor = (value) => {
        const parts = value.split('.')

        return parts.some((part, index) => {
            if (index === parts.length - 1) return false

            const ancestor = parts.slice(0, index + 1).join('.')
            return selectedValues.indexOf(ancestor) !== -1
        })
    }

    const getLabelClassName = (isInheritedSelected) => classNames('trip-select-list__label', {
        'trip-select-list__label--inherited-selected': isInheritedSelected
    })

    const renderThirdLevel = (parentKey, childKey, grandchildren) => {
        if (!grandchildren) return null

        if (isArrayOfStrings(grandchildren)) {
            return (
                <ul className="trip-select-list trip-select-list--sublist trip-select-list--sublist-level-3">
                    {grandchildren.map((grandchild) => {
                        const grandchildKey = parentKey + '.' + childKey + '.' + grandchild
                        const grandchildSelected = selectedValues.indexOf(grandchildKey) !== -1
                        const grandchildInheritedSelected = hasSelectedAncestor(grandchildKey)

                        return (
                            <li key={grandchildKey} className="trip-select-list__item">
                                <label className={getLabelClassName(grandchildInheritedSelected)}>
                                    <input
                                        type="checkbox"
                                        checked={!grandchildInheritedSelected && grandchildSelected}
                                        onChange={() => handleToggle(grandchildKey)}
                                        className="trip-select-list__input"
                                    />
                                    <span></span>
                                    {grandchild}
                                </label>
                            </li>
                        )
                    })}
                </ul>
            )
        }

        if (isObject(grandchildren)) {
            return (
                <ul className="trip-select-list trip-select-list--sublist trip-select-list--sublist-level-3">
                    {Object.keys(grandchildren).map((grandchild) => {
                        const grandchildKey = parentKey + '.' + childKey + '.' + grandchild
                        const grandchildSelected = selectedValues.indexOf(grandchildKey) !== -1
                        const grandchildInheritedSelected = hasSelectedAncestor(grandchildKey)
                        const greatGrandchildren = grandchildren[grandchild]
                        const hasGreatGrandchildren = greatGrandchildren && (isObject(greatGrandchildren) || !isArrayOfStrings(greatGrandchildren))
                        const isExpandedGrandchild = expandedChild[grandchildKey]

                        return (
                            <li key={grandchildKey} className="trip-select-list__item">
                                <label className={getLabelClassName(grandchildInheritedSelected)}>
                                    <input
                                        type="checkbox"
                                        checked={!grandchildInheritedSelected && grandchildSelected}
                                        onChange={() => handleToggle(grandchildKey)}
                                        className="trip-select-list__input"
                                    />
                                    <span></span>
                                    {grandchild}
                                </label>
                                {hasGreatGrandchildren && hasNonEmptyChildren(greatGrandchildren) && (
                                    <>
                                        <button
                                            type="button"
                                            className={`trip-select-list__toggle-btn ${isExpandedGrandchild ? 'trip-select-list__toggle-btn--expand ' : ''}`}
                                            onClick={() => toggleExpandChild(grandchildKey)}
                                        >
                                            <img src={arrowIcon} alt="" className="animation-transform" />
                                        </button>
                                        {isExpandedGrandchild && renderThirdLevel(parentKey, childKey + '.' + grandchild, greatGrandchildren)}
                                    </>
                                )}
                            </li>
                        )
                    })}
                </ul>
            )
        }

        if (Array.isArray(grandchildren)) {
            return (
                <ul className="trip-select-list trip-select-list--sublist trip-select-list--sublist-level-3">
                    {grandchildren.map((grandchild, index) => {
                        const grandchildKey = parentKey + '.' + childKey + '.' + (typeof grandchild === 'string' ? grandchild : `item_${index}`)
                        const grandchildSelected = selectedValues.indexOf(grandchildKey) !== -1
                        const grandchildInheritedSelected = hasSelectedAncestor(grandchildKey)

                        return (
                            <li key={grandchildKey} className="trip-select-list__item">
                                <label className={getLabelClassName(grandchildInheritedSelected)}>
                                    <input
                                        type="checkbox"
                                        checked={!grandchildInheritedSelected && grandchildSelected}
                                        onChange={() => handleToggle(grandchildKey)}
                                        className="trip-select-list__input"
                                    />
                                    <span></span>
                                    {typeof grandchild === 'string' ? grandchild : JSON.stringify(grandchild)}
                                </label>
                            </li>
                        )
                    })}
                </ul>
            )
        }

        return null
    }

    const renderItem = (item, children) => {
        const isSelected = selectedValues.indexOf(item) !== -1
        const isExpandedItem = expanded ? expanded : expandedParent[item];

        const childrenIsObject = children && isObject(children)
        const childrenIsArray = Array.isArray(children)
        const childrenIsArrayOfStrings = childrenIsArray && isArrayOfStrings(children)

        return (
            <li key={item} className="trip-select-list__item">
                <label className="trip-select-list__label" htmlFor={`${uniquePrefix}-${field}-${item}`}>
                    <input
                        id={`${uniquePrefix}-${field}-${item}`}
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggle(item)}
                        className="trip-select-list__input"
                    />
                    <span></span>
                    {item}
                </label>

                {children && hasNonEmptyChildren(children) && (
                    <>
                        <button
                            type="button"
                            className={`trip-select-list__toggle-btn ${isExpandedItem ? 'trip-select-list__toggle-btn--expand ' : ''}`}
                            onClick={() => toggleExpand(item)}
                        >
                            <img src={arrowIcon} alt="" className="animation-transform" />
                        </button>

                        {isExpandedItem && (
                            <ul className="trip-select-list trip-select-list--sublist">
                                {childrenIsObject ? (   
                                    Object.keys(children).map((child) => {
                                        const childKey = item + '.' + child
                                        const childSelected = selectedValues.indexOf(childKey) !== -1
                                        const childInheritedSelected = hasSelectedAncestor(childKey)
                                        const grandchildren = children[child]                                        
                                        const hasGrandchildren = grandchildren !== null && grandchildren !== undefined && (Array.isArray(grandchildren) || isObject(grandchildren))
                                        const isExpandedChildItem = expandedChild[childKey]

                                        return (
                                            <li key={childKey} className="trip-select-list__item">
                                                <label className={getLabelClassName(childInheritedSelected)}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!childInheritedSelected && childSelected}
                                                        onChange={() => handleToggle(childKey)}
                                                        className="trip-select-list__input"
                                                    />
                                                    <span></span>
                                                    {child}
                                                </label>
                                                {hasGrandchildren && hasNonEmptyChildren(grandchildren) && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            className={`trip-select-list__toggle-btn ${isExpandedChildItem ? 'trip-select-list__toggle-btn--expand ' : ''}`}
                                                            onClick={() => toggleExpandChild(childKey)}
                                                        >
                                                            <img src={arrowIcon} alt="" className="animation-transform" />
                                                        </button>
                                                        {isExpandedChildItem && renderThirdLevel(item, child, grandchildren)}
                                                    </>
                                                )}
                                            </li>
                                        )
                                    })
                                ) : childrenIsArrayOfStrings ? (                                    
                                    children.map((child) => {
                                        const childKey = item + '.' + child
                                        const childSelected = selectedValues.indexOf(childKey) !== -1
                                        const childInheritedSelected = hasSelectedAncestor(childKey)

                                        return (
                                            <li key={childKey} className="trip-select-list__item">
                                                <label className={getLabelClassName(childInheritedSelected)}>
                                                    <input
                                                        type="checkbox"
                                                        checked={!childInheritedSelected && childSelected}
                                                        onChange={() => handleToggle(childKey)}
                                                        className="trip-select-list__input"
                                                    />
                                                    <span></span>
                                                    {child}
                                                </label>
                                            </li>
                                        )
                                    })
                                ) : childrenIsArray ? (                                    
                                    children.flatMap((child, index) => {
                                        if (isObject(child)) {
                                            return Object.keys(child).map((childKey) => {
                                                const fullChildKey = item + '.' + childKey
                                                const childSelected = selectedValues.indexOf(fullChildKey) !== -1
                                                const childInheritedSelected = hasSelectedAncestor(fullChildKey)
                                                const grandchildren = child[childKey]
                                                const hasGrandchildren = grandchildren && (isObject(grandchildren) || !isArrayOfStrings(grandchildren))
                                                const isExpandedChildItem = expandedChild[fullChildKey]

                                                return (
                                                    <li key={fullChildKey} className="trip-select-list__item">
                                                        <label className={getLabelClassName(childInheritedSelected)}>
                                                            <input
                                                                type="checkbox"
                                                                checked={!childInheritedSelected && childSelected}
                                                                onChange={() => handleToggle(fullChildKey)}
                                                                className="trip-select-list__input"
                                                            />
                                                            <span></span>
                                                            {childKey}
                                                        </label>
                                                        {hasGrandchildren && hasNonEmptyChildren(grandchildren) && (
                                                            <>
                                                                <button
                                                                    type="button"
                                                                    className={`trip-select-list__toggle-btn ${isExpandedChildItem ? 'trip-select-list__toggle-btn--expand ' : ''}`}
                                                                    onClick={() => toggleExpandChild(fullChildKey)}
                                                                >
                                                                    <img src={arrowIcon} alt="" className="animation-transform" />
                                                                </button>
                                                                {isExpandedChildItem && renderThirdLevel(item, childKey, grandchildren)}
                                                            </>
                                                        )}
                                                    </li>
                                                )
                                            })
                                        } else {
                                            const childKey = item + '.' + (typeof child === 'string' ? child : `item_${index}`)
                                            const childSelected = selectedValues.indexOf(childKey) !== -1
                                            const childInheritedSelected = hasSelectedAncestor(childKey)

                                            return (
                                                <li key={childKey} className="trip-select-list__item">
                                                    <label className={getLabelClassName(childInheritedSelected)}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!childInheritedSelected && childSelected}
                                                            onChange={() => handleToggle(childKey)}
                                                            className="trip-select-list__input"
                                                        />
                                                        <span></span>
                                                        {typeof child === 'string' ? child : JSON.stringify(child)}
                                                    </label>
                                                </li>
                                            )
                                        }
                                    })
                                ) : null}
                            </ul>
                        )}
                    </>
                )}
            </li>
        )
    }

    return (
        <ul className="trip-select-list">
            {Array.isArray(data)
                ? data.map(item => renderItem(item))
                : Object.keys(data).map(parent => renderItem(parent, data[parent]))}
        </ul>
    )
}

NestedCheckboxList.propTypes = {
    field: PropTypes.string.isRequired,
    data: PropTypes.oneOfType([
        PropTypes.array,
        PropTypes.object
    ]).isRequired,
    expanded: PropTypes.bool,
    uniquePrefix: PropTypes.string.isRequired,
    selected: PropTypes.string,
    onChange: PropTypes.func.isRequired
}

export default NestedCheckboxList
